import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from '@angular/core';

import videojs from 'video.js';

import { DOCUMENT } from "@angular/common";
import { AuthUrlService } from 'auth';
import { ENVIRONMENT, Logger } from 'shared';
import Player from 'video.js/dist/types/player';
import { Media, MediaLink } from '../../models/media';

export interface VideoTemplateContext {
  id: string;
  controls: boolean;
}

// https://www.w3.org/2010/05/video/mediaevents.html
// https://gist.github.com/alecsgone/a6db03bade4dc405a61c63294a64f97a

@Component({
    selector: 'video-player',
    templateUrl: './video-player.component.html',
    styleUrls: ['./video-player.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true
})
export class VideoPlayerComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {

  private static instanceCounter: number = 0;
  protected instanceId: number = VideoPlayerComponent.instanceCounter++;

  @Input()  inline     = true;
  @Input()  autoplay   = false;
  @Input()  controls   = true;
  @Input()  muted      = false;
  @Input()  preload    = 'auto';
  protected _media: Media | null = null;
  protected pendingPlay = false;

  @Output() fullscreen      = new EventEmitter<boolean>();
  @Output() userActive      = new EventEmitter<boolean>();
  @Output() playing         = new EventEmitter<boolean>();
  @Output() loadedData      = new EventEmitter<boolean>();
  @Output() loadedMetaData  = new EventEmitter<boolean>();

  @ViewChild('videoTemplate') videoTemplate!: TemplateRef<VideoTemplateContext>;
  protected video: HTMLVideoElement | null = null;

  protected player: Player | null = null;
  protected currentPlayerOptions: any;

  protected logger = new Logger('VideoPlayerComponent');

  constructor(
              protected element: ElementRef,
              protected viewContainerRef: ViewContainerRef,
              protected changeDetector: ChangeDetectorRef,
              protected authUrlService: AuthUrlService,
              protected elementRef: ElementRef,
              @Inject(DOCUMENT) protected document: Document,
              @Inject(ENVIRONMENT) protected environment: any) {
  }

  ngOnInit() {
    this.logger.debug('ngOnInit');
  }

  ngAfterViewInit() {
    this.logger.debug('ngAfterViewInit');
  }

  ngOnDestroy() {
    // https://docs.videojs.com/docs/guides/removing-players.html
    this.logger.debug('onDestroy', this.player);
    if (this.player) {
      this.player.dispose();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.logger.debug('ngOnChanges', changes['media']);
  }

  createVideoJsPlayer(element: any, options?: any): Promise<Player> {
    let resolvePlayer: (value: Player) => void = () => {};
    const promise: Promise<Player> = new Promise(resolve => resolvePlayer = resolve);
    let player: Player = videojs(element, options);

    player.on('timeupdate', (event: any) => {
      this.logger.debug('player -> timeupdate', event);
    });
    player.on('fullscreenchange', () => {
      this.logger.debug('player -> fullscreenchange '+player.isFullscreen());
      this.fullscreen.emit(player.isFullscreen());
    });
    // player controlBar visibility is driven by user active/inactive state
    // https://docs.videojs.com/docs/api/player.html#Eventsuseractive
    player.on('useractive', (event: any) => {
      this.logger.debug('player -> useractive', event);
      this.userActive.emit(true);
    });
    player.on('userinactive', (event: any) => {
      this.logger.debug('player -> userinactive', event);
      this.userActive.emit(false);
    });
    player.on('loadeddata', (event: any) => {
      this.logger.debug('player -> loadeddata', event);
      let seconds  = Math.round(player.currentTime() || 0);
      let duration = Math.round(player.duration() || 0);
      this.logger.debug("loaded second:"+seconds+" of:"+duration);
      // angular.element(containerElement).removeClass("loading");
      if (this.autoplay || this.pendingPlay) {
        window.setTimeout(() => {
          if (player && player.paused()) {
            player.play()
              ?.catch(error => this.logger.error('autoplay', error))
              .finally(() => this.pendingPlay = false)
          }
        },200);
      }
      this.loadedData.emit(true);
    });
    player.on('loadedmetadata', (event: any) => {
      this.logger.debug('player -> loadedmetadata', event);
      this.loadedMetaData.emit(true);
    });
    // when video ends it is also inherently paused
    player.on('pause', (event: any) => {
      this.logger.debug('player -> pause', event);
      this.playing.emit(false);
    });
    player.on('play', (event: any) => {
      this.logger.debug('player -> play', event);
      this.playing.emit(true);
    });
    // player.on('ended', function() {
    //   player.currentTime(0);
    //   player.exitFullscreen();
    // });
    resolvePlayer(player);
    return promise;
  }

  playerOptions(withCredentials = false): any { // videojs.PlayerOptions { videojs type defs are not youtube-friendly
    const options: any = {
      // Youtube IFrame Player API (managed by videojs youtube tech)
      // autoplay 0/1 while regular html5 video expects these params to be booleans
      // this discrepancy is not handled by the youtube tech so we have to adapt the values here.
      autoplay: this.autoplay,
      bigPlayButton: true,
      responsive: true,
      controlBar: {
        subsCapsButton: false,
        subtitlesButton: false,
        fullscreenToggle: false
      },
      withCredentials: withCredentials
    };
    // if (this.platform.is('desktop')) {
      (options.controlBar).fullscreenToggle = true;
    // }
    options['playsinline'] = this.inline;
  
    // fix for frame jumping when playing hls video on android
    // https://github.com/videojs/videojs-contrib-hls/issues/1434
    // still use native hls safari support as some videos fail to start when
    // the videojs polyfill is used on these platforms

    // if (this.platform.is('ios') ||
    //    (this.platform.is('macos') && this.isSafari)) {
    // if (this.platform.isSafari()) {
    if (this.isSafari) {
      //only safari (and edge) have native hls for now
      return options;
    } else {
      options.techOrder = options.techOrder || [];
      options.techOrder.push('html5');
      options.html5 = {
        // videojs v7.*: use hls options
        // hls: {
        //   withCredentials: withCredentials,
        //   overrideNative: true
        // },
        //videojs 8.*: use vhs (videojs-http-streaming) options
        //instead of hls options
        vhs: {
          withCredentials: withCredentials,
          overrideNative: true
        },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      };
    }
    return options;
  }

  @Input()
  set media(media: Media) {
    let sources = [];
    let poster = null;
    this.pendingPlay = false;
    if (media) {
      this._media = media;
      const normalizeLink = (link: string): string => {
        link = `${link.startsWith('/') ? `${this.environment.serverUrl}${link}` : link}`;
        const url = new URL(link);
        const hasParameters = url.searchParams.toString().length>0;
        this.logger.log("LINK$1", link, hasParameters);
        link = `${link}${hasParameters ? '&' : '?'}ngsw-bypass`;
        this.logger.log("LINK$2",link, hasParameters);
        return this.authUrlService.rewrite(link);
      };

      const link: MediaLink = media.links?.[0];
      if (link) {
        if (Media.isVideo(media)) {
          sources.push({src: normalizeLink(link.src), type: link.mimeType || 'application/x-mpegURL'});
          // An empty cover means "no poster" (e.g. an explorer video whose
          // server frame failed): new URL('') would throw and take the whole
          // player down with it.
          poster = media.cover?.src ? normalizeLink(media.cover.src) : null;
        } else if (Media.isAudio(media)) {
          sources.push({src: normalizeLink(`${link.src}?contentType=${encodeURIComponent(link.mimeType || 'audio/mpeg')}`), type:link.mimeType || 'audio/mpeg'});
        }
      }
    }

    if (sources.length > 0) {
       // TODO:  ngOnInit() and therefore Input properties are not set yet - wait for next tick
       // Implement a better solution for this!!!
        new Promise<void>((resolve, reject) => {
          window.setTimeout(() => { resolve() }, 0)
        }).then(() => {
          const withCredentials = !!sources.find(source => source.src.startsWith(this.environment.serverUrl));
          const playerOptions = this.playerOptions(withCredentials);
          const compatible: boolean = this.isPlayerCompatible(playerOptions, this.currentPlayerOptions);
          if (!this.player || !compatible) {
            // https://github.com/videojs/video.js/issues/4397
            if (this.player) {
              this.player.dispose();
              this.viewContainerRef.clear();
            }
            this.changeDetector.markForCheck();
            playerOptions.sources = sources;
            const embeddedViewRef = this.viewContainerRef.createEmbeddedView(
              this.videoTemplate,
              { id: `${this.instanceId}`, controls: this.controls }
            );
            // search for video element because at least on ios production build it is not always the first node
            const videoNode = this.video = embeddedViewRef.rootNodes.find(node => 'video'==node.nodeName.toLowerCase());
            this.createVideoJsPlayer(videoNode, this.currentPlayerOptions = playerOptions)
              .then((player) => {
                player.poster(poster || undefined);
                this.player = player;
                return player;
              });
        } else {
          this.player.pause();
          this.player.src(sources);
          this.player.poster(poster || undefined);
        }
        });
    }
  }

  isFullscreen() : boolean {
    return !!this.player?.isFullscreen();
  }

  pause() {
    this.player?.pause();
  }

  play(muted = false) {
    if (this.player) {
      // https://stackoverflow.com/questions/36803176/how-to-prevent-the-play-request-was-interrupted-by-a-call-to-pause-error
      const playing =
         (this.player.currentTime() || 0) > 0 &&
        !this.player.paused() &&
        !this.player.ended()  &&
         this.player.readyState() == 2 //videojs.ReadyState?.HaveCurrentData;
      if (!playing) {
        if (muted) {
          this.player.volume(0);
        }
        this.player.play()?.catch(error => this.logger.error(error));
      }
    } else {
      this.pendingPlay = true;
    }
  }

  currentTime(time?:number): number {
    if (time!==undefined && time>=0) {
      this.player?.player().currentTime(time);
    }
    return this.player?.player().currentTime() || 0;
  }

  paused() : boolean {
    return !!this.player?.paused();
  }

  ended() : boolean {
    return !!this.player?.ended();
  }

  isPlayerCompatible(requiredPlayerOptions: any, currentPlayerOptions: any): boolean {
    return false; //isEqual(requiredPlayerOptions, currentPlayerOptions); // todo: if used later - omit sources in comparison!
  }

  get isSafari(): boolean {
    const   userAgent = navigator.userAgent || '';
    return  userAgent.indexOf('Safari') != -1 &&
            userAgent.indexOf('Chrome') == -1;
  }
}
